import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "antd";
import { UserOutlined, CloseOutlined } from "@ant-design/icons";
import axiosInstance from "../services/axios";
import whatsappIcon from "../assets/icons/whatsapp.svg";
import phoneIcon from "../assets/icons/phone.svg";
import zillaParishad from "../assets/images/zillaParishad.svg";
import panchayatSamiti from "../assets/images/header/samithiLogo.svg";
import "./LogoCard.css";

export default function LogoCard({ className = "" }) {
  const { t, i18n } = useTranslation();
  const [activePopup, setActivePopup] = useState(null);
  const [districtData, setDistrictData] = useState([]);
  const [panchayatData, setPanchayatData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingDistrict, setLoadingDistrict] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingPanchayat, setLoadingPanchayat] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [errorDistrict, setErrorDistrict] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [errorPanchayat, setErrorPanchayat] = useState(null);

  // Fetch Zilla Parishad officers with tags=district
  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === "mr" ? "Marathi" : "English";
    setLoadingDistrict(true);

    axiosInstance
      .get("/logo-card-officers/", {
        params: { language: languageParam, tags: "district" },
      })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) {
          data = data.results || data.data || Object.values(data) || [];
        }

        if (!Array.isArray(data) || data.length === 0) {
          setDistrictData([]);
          return;
        }

        // If `order` (or similar) exists on all items, sort by it; otherwise preserve API order
        const orderKeyCandidates = ["order", "sort_order", "position"];
        const orderKey = orderKeyCandidates.find((k) =>
          data.every((item) => item[k] !== undefined)
        );
        const ordered = orderKey
          ? [...data].sort(
              (a, b) => (Number(a[orderKey]) || 0) - (Number(b[orderKey]) || 0)
            )
          : data;

        const mapped = ordered.map((d) => ({
          id: d.id,
          name: d.name || "-",
          title: d.title || d.designation || "-",
          avatarUrl: d.avatarUrl || null,
          contact: d.contact || null,
          whatsapp: d.whatsapp || null,
          tags: d.tags || null,
          language: d.language || null,
        }));

        setDistrictData(mapped.length ? mapped : []);
      })
      .catch((err) => {
        console.error("Failed to load Zilla Parishad data:", err);
        if (isMounted) setErrorDistrict(err.message || "Failed to load data");
      })
      .finally(() => {
        if (isMounted) setLoadingDistrict(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Fetch Panchayat Samiti officers with tags=panjayat
  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === "mr" ? "Marathi" : "English";
    setLoadingPanchayat(true);

    axiosInstance
      .get("/logo-card-officers/", {
        params: { language: languageParam, tags: "panjayat" },
      })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) {
          data = data.results || data.data || Object.values(data) || [];
        }

        if (!Array.isArray(data) || data.length === 0) {
          setPanchayatData([]);
          return;
        }

        // If `order` (or similar) exists on all items, sort by it; otherwise preserve API order
        const orderKeyCandidates = ["order", "sort_order", "position"];
        const orderKey = orderKeyCandidates.find((k) =>
          data.every((item) => item[k] !== undefined)
        );
        const ordered = orderKey
          ? [...data].sort(
              (a, b) => (Number(a[orderKey]) || 0) - (Number(b[orderKey]) || 0)
            )
          : data;

        const mapped = ordered.map((d) => ({
          id: d.id,
          name: d.name || "-",
          title: d.title || d.designation || "-",
          avatarUrl: d.avatarUrl || null,
          contact: d.contact || null,
          whatsapp: d.whatsapp || null,
          tags: d.tags || null,
          language: d.language || null,
        }));

        setPanchayatData(mapped.length ? mapped : []);
      })
      .catch((err) => {
        console.error("Failed to load Panchayat Samiti data:", err);
        if (isMounted) setErrorPanchayat(err.message || "Failed to load data");
      })
      .finally(() => {
        if (isMounted) setLoadingPanchayat(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
  
  const logoCardData = [
    {
      id: 1,
      logo: zillaParishad,
      altText: "Zilla Parishad Logo",
      titleKey: "Zilla Parishad Ratnagiri",
      fallbackTitle: "जिल्हा परिषद रत्नागिरी"
    },
    {
      id: 2,
      logo: panchayatSamiti,
      altText: "Panchayat Samiti Logo", 
      titleKey: "Panchayat Samiti Guhagar",
      fallbackTitle: "पंचायत समिती गुहागर"
    }
  ];

  const handleCardClick = (cardId) => {
    setActivePopup(cardId);
  };

  const handleClosePopup = () => {
    setActivePopup(null);
  };

  const getOfficersData = (cardId) => {
    if (cardId === 1) {
      return districtData;
    }
    if (cardId === 2) {
      return panchayatData;
    }
    return [];
  };

  const getPopupTitle = (cardId) => {
    return cardId === 1 ? "जिल्हा परिषद रत्नागिरी" : "पंचायत समिती गुहागर";
  };

  return (
    <div className={`gap-2 logo-card-section max-sm:gap-1 ${className}`}>
      <div className="logo-cards-container">
        {logoCardData.map((item) => (
          <div key={item.id} className="logo-card-item" onClick={() => handleCardClick(item.id)}>
            <div className="district-card">
              <div
                className="district-content flex flex-col max-sm:gap-2 max-sm:items-stretch max-sm:text-center sm:flex-row sm:items-center w-full gap-4"
                style={{ padding: 16 }}
              >
                <div className="district-logo-wrap flex-shrink-0 flex items-center justify-center max-sm:mb-2" style={{ height: '100%' }}>
                  <img
                    src={item.logo}
                    alt={item.altText}
                    className="district-logo"
                    style={{ height: 48, width: 48, minWidth: 48, minHeight: 48 }}
                  />
                </div>
                <span
                  className="district-title"
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                    flex: 1,
                    fontWeight: 600,
                    fontSize: 18,
                    margin: 0,
                    padding: 0
                  }}
                >
                  {t(item.titleKey) || item.fallbackTitle}
                </span>
                <button
                  className="district-button max-sm:mt-2 max-sm:w-full"
                  style={{
                    flexShrink: 0,
                    marginLeft: 12,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {t('See All Officers') || 'सर्व पदाधिकारी पहा'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Popup Modal */}
      {activePopup && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup-container" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="popup-header">
              <div className="popup-header-content">
                <img loading="lazy" 
                  src={logoCardData.find(card => card.id === activePopup)?.logo} 
                  alt="Logo" 
                  className="popup-logo"
                />
                <h2 className="popup-title">{getPopupTitle(activePopup)}</h2>
              </div>
              <button className="popup-close-btn" onClick={handleClosePopup}>
                <CloseOutlined />
              </button>
            </div>

            {/* Subtitle - Only for Zilla Parishad */}
            {activePopup === 1 && (
              <>
                <p className="popup-subtitle">सरकारी ग्राधिकरण पदानुक्रम</p>
                <p className="popup-description">
                  तुमच्या लोकप्रतिनिधींना आणि प्रमुख प्रशासकीय अधिकाऱ्यांना जाणून घ्या
                </p>
              </>
            )}

            {/* Officers List */}
            <div className="popup-officers-list">
              {getOfficersData(activePopup).map((admin, index) => (
                <div
                  key={admin.id || index}
                  className="admin-relative admin-flex admin-flex-col admin-w-full max-w-md popup-officer-item"
                >
                  {/* Officer Card */}
                  <div className="admin-flex admin-w-full admin-max-w-md align-items-center">
                    <Avatar
                      size={activePopup === 1 ? 47 : 90}
                      src={admin.avatarUrl}
                      icon={<UserOutlined />}
                      className={`logocard-avatar-${activePopup === 1 ? 'zilla' : 'panchayat'}`}
                    />
                    <div className="admin-flex admin-flex-col admin-justify-center admin-ml-negative-50 admin-border admin-border-white admin-bg-white admin-pl-16 admin-rounded-full admin-w-full admin-max-w-md">
                      <span className="text-[14px] text-[#212121] font-semibold">
                        {admin.name}
                      </span>
                      <span className="admin-text-gray mb-2 admin-w-full text-[12px] mt-1 text-[#727272] font-medium leading-20">
                        {admin.title}
                      </span>

                      {/* Contact Buttons - Only for Panchayat Samiti */}
                      {activePopup === 2 && (admin.contact || admin.whatsapp) && (
                        <div className="officer-contact-buttons">
                          {admin.whatsapp && (
                            <button 
                              className="whatsapp-btn"
                              onClick={() => {
                                const waNumber = admin.whatsapp.replace(/[^0-9]/g, "");
                                const waMessage = encodeURIComponent("Hello");
                                window.open(`https://wa.me/${waNumber}?text=${waMessage}`, '_blank');
                              }}
                            >
                              <img loading="lazy" src={whatsappIcon} alt="WhatsApp" className="media-contact-icon" />
                              व्हाट्सअप<span className="mobile-hide-text"> करा</span>
                            </button>
                          )}
                          {admin.contact && (
                            <button 
                              className="call-btn"
                              onClick={() => {
                                const phone = admin.contact.replace(/[^0-9]/g, "");
                                window.open(`tel:${phone}`, '_self');
                              }}
                            >
                              <img loading="lazy" src={phoneIcon} alt="Call" className="media-contact-icon" />
                              फोन<span className="mobile-hide-text"> करा</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index !== getOfficersData(activePopup).length - 1 && (
                    <div className="admin-top-full admin-w-2-5 admin-h-7 admin-bg-black admin-self-center" />
                  )}
                </div>
              ))}
            </div>

            {/* Footer Button - Only for Zilla Parishad */}
            {activePopup === 1 && (
              <div className="popup-footer">
                <button 
                  className="popup-footer-btn"
                  onClick={() => window.open('https://zpratnagiri.gov.in/', '_blank')}
                >
                  जिल्हा परिषद रत्नागिरी अधिकाऱ्यांचे वेबसाइट
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
